import WebFont from 'webfontloader'
import * as Sentry from '@sentry/core'
import type { PresentationStyle } from 'paintscout'
import { getPrefabStyles } from '@paintscout/util/database/fixtures/presentations/prefabStyles'
import { CURATED_FONT_OPTIONS, GOOGLE_FONT_CATALOG_BY_VALUE, findFontOption } from './googleFonts/googleFontCatalog'
import { isPreviewSubsetFace } from './googleFonts/unicodeRange'

const FONT_VARIANTS = '300,300i,400,400i,500,500i'

/**
 * The family every presentation font degrades to. `getPresentationTheme` appends it to each stack
 * it emits, and it's always in the load set, so a family that fails to fetch from Google renders
 * as Inter instead of the browser's default serif/sans.
 */
export const FALLBACK_FONT_FAMILY = 'Inter'

// CSS generic families, plus the theme's `default` and `alt` markers (which name one of the
// style's two global faces rather than a face of their own). None are fetchable from Google.
const NON_LOADABLE_FAMILIES = new Set([
  'default',
  'alt',
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'inherit',
  'initial',
  'unset'
])

// System faces the picker offers that WebFont can't (or shouldn't) fetch — see CURATED_FONT_OPTIONS.
const NON_GOOGLE_FAMILIES = new Set(
  CURATED_FONT_OPTIONS.filter((option) => !option.isGoogleFont).map((option) => option.label.toLowerCase())
)

function titleCase(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ')
}

/**
 * Turns a stored CSS font stack (`'source sans 3, sans-serif'`) into the Google Fonts family label
 * WebFont expects (`'Source Sans 3'`), or `null` when the value isn't a Google-hosted family.
 *
 * The catalog is preferred over title-casing the stored value because it carries Google's own
 * casing (e.g. `'M PLUS 1p'`), which title-casing can't reproduce.
 */
export function resolveFontFamilyLabel(value: string): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null

  const first = trimmed
    .split(',')[0]
    .trim()
    .replaceAll(/^['"]|['"]$/g, '')
    .trim()
  if (!first) return null

  const lowered = first.toLowerCase()
  // checked before the catalog: Roboto is in Google's catalog but is served to presentations as a
  // system face, so requesting it from Google would be a wasted round trip
  if (NON_LOADABLE_FAMILIES.has(lowered) || NON_GOOGLE_FAMILIES.has(lowered)) return null

  const catalogEntry = GOOGLE_FONT_CATALOG_BY_VALUE[trimmed.toLowerCase()]
  if (catalogEntry) return catalogEntry.label

  return titleCase(first)
}

/**
 * The human-readable font name a style's slot actually renders with — resolving the `'default'`/
 * `'alt'` sentinels (which name one of the style's two global faces rather than a face of their
 * own) before handing the result to `resolveFontFamilyLabel`. Presentation style presets carry an
 * internal codename (`presetTitle`, e.g. `'Mari'`) that means nothing to the user, so any UI
 * showing "which fonts" a preset uses should read this instead.
 */
export function resolveTypographySlotFontFamily(
  style: PresentationStyle,
  slot: 'heading1' | 'heading2' | 'heading3' | 'body'
): string | null {
  const slotFontFamily = style?.[slot]?.fontFamily as string | undefined
  if (!slotFontFamily) return null

  const fontFamily =
    slotFontFamily === 'default'
      ? style.fontFamily
      : slotFontFamily === 'alt'
        ? style.altFontFamily || style.fontFamily
        : slotFontFamily
  if (!fontFamily) return null

  // findFontOption (not resolveFontFamilyLabel) so curated non-Google faces get their real label
  // too — e.g. 'roboto, sans-serif' is stored lowercase and isn't fetched from Google, but it's
  // still a known curated option ('Roboto') rather than an unrecognized value to title-case.
  return findFontOption(fontFamily)?.label ?? fontFamily.split(',')[0].trim()
}

/** Every font family the built-in presentation presets can render with. */
function getPrefabFontFamilyValues(): string[] {
  const values: string[] = []
  const push = (value?: string) => {
    if (value && !values.includes(value)) values.push(value)
  }
  getPrefabStyles().forEach((style) => {
    push(style.fontFamily)
    push(style.altFontFamily)
    ;['body', 'heading1', 'heading2', 'heading3'].forEach((typog) => push(style[typog]?.fontFamily))
  })
  return values
}

let cachedDefaultFamilies: string[] | null = null

/**
 * The families loaded when no explicit list is given — every preset's fonts plus the curated picker
 * options plus the fallback. Derived rather than hardcoded: the presets pull from the full Google
 * catalog now, and a stale hardcoded list left their tiles rendering in the generic fallback face.
 */
export function getDefaultPresentationFontFamilies(): string[] {
  if (!cachedDefaultFamilies) {
    cachedDefaultFamilies = dedupeLabels([
      ...getPrefabFontFamilyValues(),
      ...CURATED_FONT_OPTIONS.map((option) => option.value),
      FALLBACK_FONT_FAMILY
    ])
  }
  return cachedDefaultFamilies
}

function dedupeLabels(values: string[]): string[] {
  const labels: string[] = []
  values.forEach((value) => {
    const label = resolveFontFamilyLabel(value)
    if (label && !labels.includes(label)) labels.push(label)
  })
  return labels
}

const handleLoaded = (win: Window) => {
  if (win.sessionStorage) {
    win.sessionStorage.setItem('fontsLoaded', 'true')
  }
}

/**
 * A family Google couldn't serve. Nothing to repair at runtime — the stacks already end in
 * `Inter`, so the browser has already fallen back. Log it so a broken family name in a saved
 * style is visible instead of silently rendering wrong.
 */
function handleFontInactive(familyName: string) {
  const error = new Error(`Failed to load Google font "${familyName}" — falling back to ${FALLBACK_FONT_FAMILY}`)
  console.error(error.message, { fontFamily: familyName, error })
  Sentry.captureException(error, { tags: { presentationFont: familyName } })
}

export function loadPresentationFonts(args: { win: Window; doc: Document; fontFamilies?: string[] }) {
  const { doc, fontFamilies, win } = args
  const families = fontFamilies
    ? dedupeLabels([...fontFamilies, FALLBACK_FONT_FAMILY])
    : getDefaultPresentationFontFamilies()

  if (!families.length) return null
  if (fontLoadChecker({ families, doc, win })) {
    return null
  }

  WebFont.load({
    google: {
      families: families.map((family) => `${family}:${FONT_VARIANTS}`)
    },
    // @ts-ignore
    context: win,
    active: () => handleLoaded(win),
    // every requested family failed — still mark the pass done so callers stop waiting on it
    inactive: () => handleLoaded(win),
    fontinactive: (familyName: string) => handleFontInactive(familyName)
  })
}

function fontLoadChecker(args: { families: string[]; doc: Document; win: Window }) {
  const { doc, families, win } = args
  let allLoaded = true
  const docFonts: string[] = []
  doc.fonts.forEach((font) => {
    // The font picker previews each family's name from a glyph subset it adds to this same document
    // when the presentation renders outside an iframe. That subset isn't the family, so counting it
    // would skip the real request and leave the presentation in the fallback face for every letter
    // outside the name.
    if (isPreviewSubsetFace(font)) return
    const family = font.family.toLowerCase().replaceAll(/^['"]|['"]$/g, '')
    if (!docFonts.includes(family)) docFonts.push(family)
  })
  families.forEach((family) => {
    const lowered = family.toLowerCase()
    // doc.fonts.check() returns true for fonts that don't (yet) exist in FontFaceSet, hence the
    // second check against FontFaceSet (doc.fonts)
    if (!checkFontFamily(doc, family)) allLoaded = false
    if (!docFonts.includes(lowered)) allLoaded = false
  })
  if (allLoaded) handleLoaded(win)
  return allLoaded
}

/**
 * `doc.fonts.check()` takes a CSS font shorthand, so the family has to be quoted: an unquoted
 * `12px source sans 3` is invalid CSS (`3` isn't a valid identifier) and throws a SyntaxError that
 * used to escape the whole effect and stop every font from loading.
 */
function checkFontFamily(doc: Document, family: string): boolean {
  try {
    return (doc as any).fonts.check(`12px "${family.replaceAll('"', '')}"`)
  } catch (err) {
    console.error(`Could not check whether font "${family}" is loaded`, { fontFamily: family, error: err })
    return false
  }
}
